import { Card, CardContent, EmptyState, Flex, Icons, Paragraph } from "@ir/ui";
import {
  AccordianCustom,
  LoadingSpinner,
} from "@src/presentation/components/common";
import { usePurchasersData } from "@src/presentation/providers/PurchasersDataProvider";
import { AddPurchaserRow } from "./AddPurchaserRow";
import { AssociatedBrandsSection } from "./AssociatedBrandsSection";
import { PurchaserAccordionTitle } from "./PurchaserAccordionTitle";
import type { PurchaserFormMode } from "./purchaserFormUtils";
import "./PurchasersAccordion.scss";

interface PurchasersContentProps {
  formMode: PurchaserFormMode;
  editingPurchaserId: number | null;
  onCloseForm: () => void;
  onEditPurchaser: (purchaserId: number) => void;
}

function renderAssociatedBrandsContent(purchaserId: number) {
  return (
    <Flex
      direction="column"
      gap={0.5}
      className="px-2 pb-2"
      style={{ width: "100%" }}
    >
      <AssociatedBrandsSection purchaserId={purchaserId} />
    </Flex>
  );
}

export const PurchasersContent = ({
  formMode,
  editingPurchaserId,
  onCloseForm,
  onEditPurchaser,
}: PurchasersContentProps) => {
  const { purchasers, loading, error } = usePurchasersData();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <Paragraph className="text-danger m-0">{error}</Paragraph>;
  }

  const renderPurchasersSection = () => {
    if (purchasers.length > 0) {
      return (
        <AccordianCustom
          key={String(editingPurchaserId ?? "none")}
          className="mb-2"
          items={purchasers.map((purchaser) => {
            const isEditing =
              formMode === "edit" &&
              editingPurchaserId === purchaser.purchaserId;

            return {
              key: String(purchaser.purchaserId),
              title: (
                <PurchaserAccordionTitle
                  purchaser={purchaser}
                  isEditing={isEditing}
                  onEdit={() => onEditPurchaser(purchaser.purchaserId)}
                  onCancelEdit={onCloseForm}
                />
              ),
              content: isEditing
                ? null
                : renderAssociatedBrandsContent(purchaser.purchaserId),
            };
          })}
        />
      );
    }

    if (formMode === "add") {
      return null;
    }

    return (
      <EmptyState
        icon={Icons.InfoCircle}
        title="No purchasers found"
        description="Add a purchaser to get started."
        className="border-1 border-color-grey-25 bg-white"
      />
    );
  };

  return (
    <Card variant="flat">
      <CardContent className="p-0">
        {formMode === "add" ? (
          <AddPurchaserRow onCancel={onCloseForm} onCreated={onCloseForm} />
        ) : null}
        {renderPurchasersSection()}
      </CardContent>
    </Card>
  );
};
